
import { useCallback, useState, useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import type { QuizState } from "@/types/quiz";
import type { Question } from "@/types/quiz";

export function useQuizHints(
  state: QuizState,
  setState: (state: QuizState | ((prev: QuizState) => QuizState)) => void,
  questions: Question[],
) {
  const [isLoadingHint, setIsLoadingHint] = useState(false);
  
  // Reset hint when question changes
  useEffect(() => {
    setState(prev => ({
      ...prev,
      showHint: false,
      currentHint: null
    }));
  }, [state.questionIndex, setState]);

  const handleShowHint = useCallback(async () => {
    if (!questions[state.questionIndex]) {
      console.error('No current question found at index:', state.questionIndex);
      toast.error("Cannot load hint: question not found");
      return;
    }
    
    // Check if hint already exists and is shown
    if (state.showHint && state.currentHint) {
      console.log('Hint already available:', state.currentHint);
      return; // Hint already shown, no need to fetch again
    }
    
    setIsLoadingHint(true);
    
    try {
      const currentQuestion = questions[state.questionIndex];
      console.log('Requesting hint for question:', {
        questionId: currentQuestion.id,
        question: currentQuestion.question.substring(0, 50) + '...',
        questionIndex: state.questionIndex
      });

      // Prepare context for hint generation without revealing the answer
      const questionContext = `This question is about ${currentQuestion.subject || 'an important concept'}. The question asks: ${currentQuestion.question}`;

      // First try with the dedicated generate-hint function
      try {
        console.log('Using dedicated hint generation function for question index:', state.questionIndex);
        
        const { data: hintData, error: hintError } = await supabase.functions.invoke('generate-hint', {
          body: { explanation: questionContext }
        });
        
        if (hintError) {
          console.error('Dedicated hint function error:', hintError);
          throw hintError;
        }
        
        if (!hintData || !hintData.hint) {
          throw new Error('Invalid response from hint generation');
        }
        
        console.log('Generated hint successfully:', hintData.hint.substring(0, 30) + '...');
        
        setState(prev => ({
          ...prev,
          showHint: true,
          currentHint: hintData.hint
        }));
        
        toast.success("Hint available!");
        return;
      } catch (directError) {
        // If dedicated function fails, try the multi-purpose AI function
        console.error('Dedicated hint function failed, trying ai-quiz-features:', directError);
        
        const { data, error } = await supabase.functions.invoke('ai-quiz-features', {
          body: {
            action: 'generateHint',
            data: {
              question: currentQuestion.question,
              attempt: state.answers[state.questionIndex],
              difficulty: state.difficulty,
              adaptiveMode: true
            }
          }
        });

        if (error) {
          console.error('Error from ai-quiz-features:', error);
          throw error;
        }

        if (!data || !data.hint) {
          throw new Error('Invalid response from AI hint generation');
        }

        setState(prev => ({
          ...prev,
          showHint: true,
          currentHint: data.hint
        }));

        toast.success("Hint available!");
      }
    } catch (error) {
      console.error('Error getting hint:', error);
      
      // Generate a simple fallback hint based on the current question
      const currentQuestion = questions[state.questionIndex];
      const fallbackHint = currentQuestion && currentQuestion.subject ? 
        `Think about key concepts in ${currentQuestion.subject} related to this question.` : 
        "Focus on the key concepts related to this question and analyze all options methodically.";
      
      setState(prev => ({
        ...prev,
        showHint: true,
        currentHint: fallbackHint
      }));
      
      toast.info("Using basic hint (AI service unavailable)");
    } finally {
      setIsLoadingHint(false);
    }
  }, [state.questionIndex, state.answers, state.difficulty, state.showHint, state.currentHint, questions, setState]);

  return {
    handleShowHint,
    isLoadingHint
  };
}
